/** The plugin manager. (The tag editor is views/TagEditorView.) */

import { engineBase } from "../api";
import { useCallback, useEffect, useState } from "react";
import { useApp } from "../core/app";
import { setPluginEnabled, syncPlugins, type EnginePlugin } from "../core/plugins";
import { Icon } from "../shell/Icons";
import { toast } from "../shell/Toast";

export function PluginsView() {
  const app = useApp();
  const [plugins, setPlugins] = useState<EnginePlugin[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => setPlugins(await syncPlugins()), []);
  useEffect(() => void load(), [load]);

  const toggle = async (p: EnginePlugin) => {
    setBusy(p.id);
    const error = await setPluginEnabled(p.id, !p.enabled);
    setBusy(null);
    if (error) toast(`${p.name}: ${error}`, "bad");
    else toast(`${p.name} ${p.enabled ? "turned off" : "turned on"}.`, "ok");
    await load();
  };

  return (
    <section className="nv-view" aria-label="Plugins">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>Plugins</h1>
          <div className="nv-sub">Plugins add screens, menu items, song actions, layouts and engine features. Turning one off removes everything it added, straight away.</div>
        </div>
        <div className="nv-actions">
          <button className="nv-btn" title="Look for plugins added since Nova started" onClick={async () => {
            await fetch(`${engineBase()}/api/plugins/rescan`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }).catch(() => null);
            await load();
          }}><Icon name="refresh" /> Reload</button>
          <button className="nv-btn" onClick={() => app.openSettings("plugin-guide")}><Icon name="help" /> Writing a plugin</button>
        </div>
      </div>
      <div className="nv-rowlist">
        {plugins === null && <div className="nv-empty">Loading…</div>}
        {plugins?.length === 0 && (
          <div className="nv-empty">
            <Icon name="plug" size={22} />
            <b>No plugins installed</b>
            <span>Put a plugin folder in Nova's data folder, under <code>plugins/</code>, or install a Python package that declares a <code>nova.plugins</code> entry point. Then press Reload.</span>
          </div>
        )}
        {plugins?.map((p) => (
          <div key={p.id} className={`nv-plugin ${p.error ? "bad" : ""}`}>
            <Icon name="plug" size={20} />
            <div className="nv-plugin-text">
              <b>{p.name} <span className="nv-dim">{p.version && `v${p.version}`}</span></b>
              <span>{p.description || <span className="nv-dim">No description.</span>}</span>
              {p.error && <span className="nv-plugin-error">{p.error}</span>}
              <span className="nv-dim">{p.source === "bundled" ? (p.optional ? "Comes with Nova · optional" : "Comes with Nova") : p.source === "package" ? "Installed package" : "Plugin folder"}{p.ui ? " · has an interface" : ""}</span>
            </div>
            {!p.id.startsWith("broken:") && !p.id.startsWith("duplicate:") && (
              <label className="nv-toggle" title={p.enabled ? "Turn off" : "Turn on"}>
                <span className="sr-only">{p.enabled ? "On" : "Off"}</span>
                <input type="checkbox" role="switch" checked={p.enabled} disabled={busy === p.id || app.offline} onChange={() => void toggle(p)} />
                <i aria-hidden="true" />
              </label>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
